import { FormattedMessage, useIntl } from 'react-intl';
import Radio from 'client/components/GDS/Radio/Radio';
import Button from 'client/components/aria/Button/Button';
import PropTypes from 'prop-types';

import styles from '../CreateEnquiryPage.module.scss';
import { useEffect, useState } from 'react';
import Details from 'client/components/GDS/Details/Details';
import useAuth from 'client/utils/auth/useAuth';

const EnquiryTypeSelectionForm = ({
    onSelectEnquiryType,
    selectedEnquiryType,
    setShowSummary,
    canSelectPlanned,
    canSelectUnplanned
}) => {
    const intl = useIntl();
    const { user } = useAuth();
    const [currentSelection, setCurrentSelection] = useState(selectedEnquiryType || null);

    useEffect(() => {
        setShowSummary(false);
    }, [setShowSummary]);

    useEffect(() => {
        setCurrentSelection(selectedEnquiryType ?? null);
    }, [selectedEnquiryType]);

    useEffect(() => {
        if (!currentSelection) {
            if (canSelectPlanned && !canSelectUnplanned) {
                setCurrentSelection('planned');
                onSelectEnquiryType('planned');
            } else if (!canSelectPlanned && canSelectUnplanned) {
                setCurrentSelection('unplanned');
            }
        }
    }, [canSelectPlanned, canSelectUnplanned, currentSelection, onSelectEnquiryType]);

    const handleSubmitForm = () => {
        if (!currentSelection) {
            return;
        }

        onSelectEnquiryType(currentSelection);
    };

    return (
        <div data-testid="enquiry-type-selection">
            <div className="govuk-form-group" aria-labelledby="id-emergency-label" data-testid="I2D-planned.unplanned.selection">
                <fieldset className="govuk-fieldset">
                    <legend className="govuk-fieldset__legend govuk-fieldset__legend--m">
                        <h2 className="govuk-fieldset__heading govuk-!-margin-bottom-4" id="id-emergency-label">
                            <FormattedMessage id='createEnquiryPage.select.enquiry.type.subtitle'/>
                        </h2>
                    </legend>
                    {user?.organisationName && (
                        <div className="govuk-body govuk-!-margin-bottom-6">
                            <p className="govuk-body">
                                <FormattedMessage
                                    id='createWorkpackPage.purpose.currentOrganisation'
                                    values={{ organisationName: user.organisationName }}
                                />
                            </p>
                        </div>
                    )}
                    <Details className="govuk-!-margin-top-10" summaryText={intl.formatMessage({ id: 'createEnquiryPage.more.info.about.enquiry.types' })}>
                        <p className="govuk-body govuk-!-margin-top-2">
                            <FormattedMessage id='createEnquiryPage.enquiry.type.info.unplanned'/>
                        </p>
                    </Details>
                    <div className='govuk-body govuk-!-margin-top-4'>
                        <FormattedMessage id='createEnquiryPage.enquiry.type.title'/>
                    </div>
                    <div id="purpose-hint" className="govuk-hint">
                        <FormattedMessage id='createEnquiryPage.enquiry.type.subtitle'/>
                    </div>
                    <div className={[styles.radio, 'govuk-radios'].join(' ')} data-module="govuk-radios" role="radiogroup" >
                        {canSelectPlanned && (
                            <Radio
                                name="UtilityEnquiryType"
                                data-testid='radio.input-planned'
                                id={'planned.work.button'}
                                label={intl.formatMessage({ id: 'createEnquiryPage.planned.work' })}
                                value={'planned'}
                                role="radio"
                                tabIndex={0}
                                onChange={() => setCurrentSelection('planned')}
                                checked={currentSelection === 'planned'}
                            />
                        )}
                        {canSelectUnplanned && (
                            <Radio
                                name="UtilityEnquiryType"
                                data-testid='radio.input-unplanned'
                                id={'unplanned.work.button'}
                                label={intl.formatMessage({ id: 'createEnquiryPage.unplanned.work' })}
                                value={'unplanned'}
                                role="radio"
                                tabIndex={0}
                                onChange={() => setCurrentSelection('unplanned')}
                                checked={currentSelection === 'unplanned'}
                            />
                        )}
                    </div>
                    {currentSelection === 'unplanned' && (
                        <div className="govuk-inset-text govuk-!-margin-top-6 govuk-grid-column-three-quarters">
                            <FormattedMessage id='createEnquiryPage.enquiry.type.unplanned.notice'/>
                        </div>
                    )}
                </fieldset>
            </div>
            <hr className="govuk-section-break govuk-section-break--l govuk-section-break--visible" />
            <div className={'govuk-button-group'}>
                <Button
                    isDisabled={!currentSelection}
                    onPress={handleSubmitForm}
                    data-testid='continueButton'
                >
                    {intl.formatMessage({ id: 'EnquiryPageForm.continueButton.label' })}
                </Button>
            </div>
        </div>
    );
};

export default EnquiryTypeSelectionForm;

EnquiryTypeSelectionForm.propTypes = {
    onSelectEnquiryType: PropTypes.func.isRequired,
    selectedEnquiryType: PropTypes.string,
    setShowSummary: PropTypes.func.isRequired,
    canSelectPlanned: PropTypes.bool,
    canSelectUnplanned: PropTypes.bool
};

EnquiryTypeSelectionForm.defaultProps = {
    selectedEnquiryType: null,
    canSelectPlanned: true,
    canSelectUnplanned: true
};
